<!--
    Step bar of a provision run (04-frontend.md §5.1/§5.2).

    Build shape after the repo original bm-billing-run-status-flow.component.html: a table with
    one row per step, status column (completed / current / pending), description, error/restart
    cell, and action column. The row order comes from CP_RUN_STATUS_ORDER (see TS) — NEVER from
    the enum ordinal.

    DOCUMENTED DEVIATION (UX1): the step bar below is a raw <table>, not a p-table. This is the
    1:1 build shape of the reference module
    (features/feature-bm/bm/component/protected-components/bm-billing-run-status-flow-component/
    bm-billing-run-status-flow.component.html:14) and deliberately carried over as-is — see the
    TS class header.

    All buttons are PrimeNG components; the layout comes exclusively from PrimeFlex utilities;
    all icons are Font Awesome (fa-solid), never PrimeIcons (UX7).
-->
@if (initialized && provisionRunDto) {
    <div class="cp-run-status-flow">

        <!-- Terminal banner: ABORTED belongs to no step of the bar (04 §5.2) and is therefore
             shown above the bar as a final statement. -->
        @if (isAborted()) {
            <p-message severity="error" styleClass="w-full mb-3">
                <div class="flex flex-column">
                    <span class="font-semibold">Provisionslauf abgebrochen</span>
                    <span class="text-sm">
                        Der Lauf wurde abgebrochen. Alle berechneten, nicht aktivierten Buchungen dieses
                        Laufs wurden verworfen. Es sind keine weiteren Schritte möglich.
                    </span>
                </div>
            </p-message>
        }

        <!-- The bar is live by default (optimistic, see the RIGHTS section of the class TSDoc),
             so this banner appears only once the SERVER has refused the right with a 403. The bar
             stays readable, only the actions are dead. -->
        @if (!runExecutionGranted) {
            <p-message severity="warn" styleClass="w-full mb-3">
                <span class="text-sm">
                    Aktionen sind gesperrt: Der Server hat das Recht „cp_runExecution“ für Sie
                    verweigert. Die Schritt-Leiste wird weiterhin angezeigt.
                </span>
            </p-message>
        }

        <!-- Header band: per 04 §5.1, "Abbrechen" belongs to NO single step (column "—"), but
             applies up to and including IN_REVIEW/CALCULATION_FAILED — hence the button sits
             here at the top instead of four times in the table. -->
        <div class="flex align-items-center justify-content-between flex-wrap gap-2 p-3 border-bottom-1 surface-border">
            <div class="flex align-items-center gap-2">
                @if (jobRunning) {
                    <i class="fa-solid fa-spinner fa-spin text-primary"></i>
                    <span class="text-sm text-600">Ein Provisions-Job läuft — Aktionen sind so lange gesperrt.</span>
                } @else {
                    <span class="text-sm text-600">
                        Aktueller Status: <span class="font-semibold text-900">{{ getStatusLabel(provisionRunDto.status) }}</span>
                    </span>
                }
            </div>

            <p-button label="Provisionslauf abbrechen"
                      icon="fa-solid fa-ban"
                      severity="danger"
                      [outlined]="true"
                      [loading]="busyAction === 'abort'"
                      [disabled]="isActionBlocked() || !isEligibleAbort(provisionRunDto.status)"
                      (onClick)="handleAbort()">
            </p-button>
        </div>

        <div class="overflow-x-auto">
            <table class="w-full cp-run-status-flow-table">
                <thead>
                    <tr class="surface-100">
                        <th class="px-3 py-3 text-left text-xs font-semibold text-500 uppercase w-8rem">Status</th>
                        <th class="px-3 py-3 text-left text-xs font-semibold text-500 uppercase">Arbeitsschritt</th>
                        <th class="px-3 py-3 text-center text-xs font-semibold text-500 uppercase w-16rem">Fehler</th>
                        <th class="px-3 py-3 text-center text-xs font-semibold text-500 uppercase w-20rem">Aktion</th>
                    </tr>
                </thead>
                <tbody>
                    @for (event of events; track event.step) {
                        <!-- A pending row is dimmed ("not its turn yet") — but NOT when it carries
                             an executable action or is in an error state. A parent element's
                             opacity cannot be un-applied on a child, so the row itself must stay
                             fully opaque (bm original, same spot). -->
                        <tr class="border-bottom-1 surface-border"
                            [ngClass]="{
                                'surface-50': event.status === 'active',
                                'opacity-60': event.status === 'pending' && !hasEnabledAction(event) && !isStepFailed(event)
                            }">

                            <!-- Status column -->
                            <td class="px-3 py-3 white-space-nowrap">
                                @if (isStepFailed(event)) {
                                    <span class="inline-flex align-items-center gap-1 px-2 py-1 border-round text-sm font-semibold bg-red-100 text-red-700">
                                        <i class="fa-solid fa-circle-xmark"></i> Fehler
                                    </span>
                                } @else {
                                    @switch (event.status) {
                                        @case ('completed') {
                                            <span class="inline-flex align-items-center gap-1 px-2 py-1 border-round text-sm font-semibold bg-green-100 text-green-700">
                                                <i class="fa-solid fa-circle-check"></i> Erledigt
                                            </span>
                                        }
                                        @case ('active') {
                                            <span class="inline-flex align-items-center gap-2 px-2 py-1 border-round text-sm font-semibold bg-primary text-white">
                                                <i [class]="event.icon"></i> Aktuell
                                            </span>
                                        }
                                        @default {
                                            <span class="inline-flex align-items-center gap-1 px-2 py-1 border-round text-sm font-semibold surface-200 text-500">
                                                Wartend
                                            </span>
                                        }
                                    }
                                }
                            </td>

                            <!-- Step column -->
                            <td class="px-3 py-3">
                                <div class="text-base"
                                     [ngClass]="{
                                         'font-bold text-primary': event.status === 'active' && !isStepFailed(event),
                                         'font-bold text-red-600': isStepFailed(event),
                                         'font-semibold text-900': event.status === 'completed',
                                         'font-medium text-500': event.status === 'pending' && !isStepFailed(event)
                                     }">
                                    {{ event.step }}. {{ event.title }}
                                </div>
                                @if (event.description) {
                                    <div class="text-sm mt-1 line-height-3"
                                         [ngClass]="{
                                             'text-primary-700': event.status === 'active' && !isStepFailed(event),
                                             'text-600': event.status !== 'active' || isStepFailed(event)
                                         }">
                                        {{ event.description }}
                                    </div>
                                }
                            </td>

                            <!-- Error + restart: its own cell, so the restart button can never be
                                 confused with the row's forward actions. -->
                            <td class="px-3 py-3 text-center">
                                @if (isStepFailed(event) && event.restartAction) {
                                    <div class="flex flex-column align-items-center gap-2">
                                        <span class="text-sm text-red-600">
                                            {{ getStatusLabel(event.failedStatus) }}
                                        </span>
                                        <p-button [label]="event.restartAction.label"
                                                  [icon]="event.restartAction.icon"
                                                  severity="danger"
                                                  [outlined]="true"
                                                  [loading]="busyAction?.startsWith('restart')"
                                                  [disabled]="isActionBlocked()"
                                                  (onClick)="event.restartAction.command?.()">
                                        </p-button>
                                    </div>
                                } @else if (showsContractErrors(event) || showsRunErrors(event)) {
                                    <!-- The real error figures of the run: contracts with outcome
                                         ERROR and open ERROR messages (step 2), run-level ERROR
                                         messages on the step the run stands on. -->
                                    <div class="flex flex-column align-items-center gap-1 text-sm">
                                        @if (showsContractErrors(event)) {
                                            @if (!hasContractErrorFigures()) {
                                                <span class="text-400">-</span>
                                            } @else if (errorContractCount === 0 && openErrorMessageCount === 0) {
                                                <span class="text-green-700">
                                                    <i class="fa-solid fa-circle-check mr-1"></i>Keine Fehler
                                                </span>
                                            } @else {
                                                <span class="font-semibold text-red-700"
                                                      pTooltip="Diese Verträge wurden in diesem Lauf nicht gebucht. Details in den Reitern „Verträge“ und „Meldungen“."
                                                      tooltipPosition="top">
                                                    <i class="fa-solid fa-circle-exclamation mr-1"></i>
                                                    {{ errorContractCount }} {{ errorContractCount === 1 ? 'Vertrag' : 'Verträge' }} mit Fehler
                                                </span>
                                                <span class="text-red-600">
                                                    {{ openErrorMessageCount }} offene {{ openErrorMessageCount === 1 ? 'Fehlermeldung' : 'Fehlermeldungen' }}
                                                </span>
                                            }
                                        }
                                        @if (showsRunErrors(event)) {
                                            <span class="text-red-600"
                                                  pTooltip="Fehlermeldungen auf Laufebene — Details im Reiter „Lauf-Meldungen“."
                                                  tooltipPosition="top">
                                                {{ openRunErrorMessageCount }} {{ openRunErrorMessageCount === 1 ? 'Lauf-Fehlermeldung' : 'Lauf-Fehlermeldungen' }}
                                            </span>
                                        }
                                    </div>
                                } @else {
                                    <span class="text-400">-</span>
                                }
                            </td>

                            <!-- Action column

                                 A step that is DONE still shows an action that is still executable.
                                 The only such action today is the accounting hand-over of step 4:
                                 03 §8.4 step 4 has the clerk set COMPLETED only AFTER handing the
                                 document to accounting, so on a COMPLETED run the step is green and
                                 the document must remain downloadable — without this branch it
                                 would disappear at exactly the moment the hand-over is confirmed.
                                 For every other step the branch changes nothing: their actions are
                                 all disabled once the step is behind the run, so hasEnabledAction()
                                 is false and the cell still renders "-". -->
                            <td class="px-3 py-3 text-center">
                                @if (!isAborted() && (event.status === 'active' || hasEnabledAction(event))) {
                                    <div class="flex justify-content-center gap-2 flex-wrap">
                                        @for (action of event.actions; track action.label) {
                                            <!-- A read-only action (a document download) is not
                                                 gated by isActionBlocked(): it needs neither the
                                                 run right nor a quiet job queue — see readOnly on
                                                 CpRunActionButton. An own-right action ("Abrechnungen
                                                 erstellen", cp_agentStatement) ignores the run right
                                                 but respects the job gate — see isActionDisabled(). -->
                                            <p-button [label]="action.label"
                                                      [icon]="action.icon"
                                                      [severity]="action.nextStep ? 'primary' : 'secondary'"
                                                      [outlined]="!action.nextStep"
                                                      [loading]="isActionLoading(action)"
                                                      [disabled]="isActionDisabled(action)"
                                                      (onClick)="action.command?.()">
                                            </p-button>
                                        }
                                    </div>
                                } @else if (event.status === 'pending') {
                                    <i class="fa-solid fa-lock text-400 text-xl"></i>
                                } @else {
                                    <span class="text-400">-</span>
                                }
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    </div>

    <!-- "Aktivieren" confirms here instead of in the one-line ConfirmationService prompt: sum,
         bookings, error contracts, M-026 warnings, last calculation, explicit acknowledgement. -->
    <cp-run-activation-confirm-dialog
            [(visible)]="activationDialogVisible"
            [provisionRunDto]="provisionRunDto"
            (accepted)="handleActivationAccepted()">
    </cp-run-activation-confirm-dialog>
}
